import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Button } from '../../components/ui/Button';
import { api } from '../../lib/api';
import { useSession } from '../../stores/session';
import type { Profile } from './types';

/**
 * Follow and unfollow, applied optimistically.
 *
 * Following is a low-stakes, high-frequency action, and waiting a round trip
 * before the button changes makes the whole app feel slow. So the cache is
 * updated immediately and rolled back if the server disagrees.
 *
 * The rollback is the part that is easy to skip and expensive to omit: without
 * it a failed request leaves the UI claiming a relationship that does not
 * exist, and the lie survives until something else refetches.
 */
export function FollowButton({ profile }: { profile: Profile }) {
  const me = useSession((s) => s.user);
  const queryClient = useQueryClient();
  const key = ['profile', profile.username];

  const mutation = useMutation({
    mutationFn: (follow: boolean) =>
      follow
        ? api.post(`/users/${profile.username}/follow`)
        : api.delete(`/users/${profile.username}/follow`),

    onMutate: async (follow: boolean) => {
      // Any refetch already in flight would land after this and overwrite the
      // optimistic value with a pre-change snapshot.
      await queryClient.cancelQueries({ queryKey: key });

      const previous = queryClient.getQueryData<Profile>(key);

      queryClient.setQueryData<Profile>(key, (old) =>
        old
          ? {
              ...old,
              is_following: follow,
              follower_count: old.follower_count + (follow ? 1 : -1),
            }
          : old,
      );

      return { previous };
    },

    onError: (_error, _follow, context) => {
      if (context?.previous) {
        queryClient.setQueryData(key, context.previous);
      }
    },

    // Reconciled against the server either way. The optimistic count is a
    // guess - somebody else may have followed in the same second - and this is
    // what makes the number settle on the truth.
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  });

  if (!me || me.id === profile.id) {
    return null;
  }

  return (
    <Button
      variant={profile.is_following ? 'secondary' : 'primary'}
      loading={mutation.isPending}
      onClick={() => mutation.mutate(!profile.is_following)}
    >
      {profile.is_following ? 'Following' : 'Follow'}
    </Button>
  );
}
